import React from 'react';
import { SectionProps } from '../types';
import SectionCompetitorResults from '../shared/SectionCompetitorResults';
import SectionMessage from '../shared/SectionMessage';
import SectionTitle from '../shared/SectionTitle';
import SectionTooltip from '../shared/SectionTooltip';
import { SectionResult } from '../../../types';
import HowToFix from '../shared/HowToFix';
import InlineModal from '../shared/InlineModal';
import AnchorLabeledList from '../shared/AnchorLabeledList';
import { anchorAttributesAndLabelForUrls, anchorAttributesForUrls } from '../utils';

export default function CdnUsageSection({ url, sectionKey, title, tooltip, relevance, passRate, result, competitorsResults }: SectionProps) {
    const hasCompetitors = competitorsResults && Object.keys(competitorsResults).length > 0;

    return (
        <div
            id={sectionKey}
            className={`report-element ${result.status}`}
        >
            <SectionTitle
                title={title}
                status={result.status}
                passRate={passRate}
            />

            <div className="report-element-description">
                <SectionMessage
                    url={url}
                    status={result.status}
                    relevance={relevance}
                    message={result.message}
                    hasCompetitors={hasCompetitors}
                />

                <CdnUsageResult
                    title={title}
                    result={result}
                />

                {hasCompetitors && (
                    <SectionCompetitorResults
                        results={competitorsResults}
                        relevance={relevance}
                    />
                )}
            </div>

            <SectionTooltip tooltip={tooltip} />
        </div>
    );
}

type CdnUsageResultProps = {
    title: string;
    result: SectionResult;
};

function CdnUsageResult({ title, result }: CdnUsageResultProps) {
    return (
        <>
            <div className="description-meta">
                {result.cdn_usage && result.cdn_usage.image && (
                    <ResourceModal
                        label="Images"
                        resource={result.cdn_usage.image}
                        modalTitle="CDN usage for image resources"
                        notFromCdnListName="Images not served from CDNs:"
                        fromCdnListName="Images served from CDNs:"
                    />
                )}
            </div>

            <div className="description-meta">
                {result.cdn_usage && result.cdn_usage.javascript && (
                    <ResourceModal
                        label="JavaScript"
                        resource={result.cdn_usage.javascript}
                        modalTitle="CDN usage for JavaScript resources"
                        notFromCdnListName="JavaScript resources not served from CDNs:"
                        fromCdnListName="JavaScript resources served from CDNs:"
                    />
                )}
            </div>

            <div className="description-meta">
                {result.cdn_usage && result.cdn_usage.css && (
                    <ResourceModal
                        label="CSS"
                        resource={result.cdn_usage.css}
                        modalTitle="CDN usage for CSS resources"
                        notFromCdnListName="CSS resources not served from CDNs:"
                        fromCdnListName="CSS resources served from CDNs:"
                    />
                )}
            </div>

            {result.how_to_fix && (
                <HowToFix
                    title={title}
                    howToFix={result.how_to_fix}
                    howToFixVideo={result.how_to_fix_video}
                />
            )}
        </>
    );
}

type ResourceModalProps = {
    label: string;
    resource: any;
    modalTitle: string;
    notFromCdnListName: string;
    fromCdnListName: string;
};

function ResourceModal({ label, modalTitle, resource, notFromCdnListName, fromCdnListName }: ResourceModalProps) {
    return (
        <InlineModal
            openLabel={label}
            modalTitle={modalTitle}
            closeLabel="Close"
        >
            {resource.not_from_cdn.length > 0 && <div className="list-title">{notFromCdnListName}</div>}
            <AnchorLabeledList list={anchorAttributesForUrls(resource.not_from_cdn)} />

            {resource.from_cdn.length > 0 && <div className="list-title">{fromCdnListName}</div>}
            <AnchorLabeledList
                list={anchorAttributesAndLabelForUrls(resource.from_cdn)}
                className="labeled"
            />
        </InlineModal>
    );
}
